import React from 'react';
import CompareShell, { AppStoreBadges } from './CompareShell';
import { Check, X, Users, Receipt, Smartphone, ArrowRight } from 'lucide-react';

const ZELLE_MATRIX = [
  {
    category: 'Clero Groups & Shared Spending',
    features: [
      {
        name: 'Monthly roommate groups (rent & bills)',
        clero: 'Auto-settles on the 1st',
        competitor: 'Not supported (1-on-1 only)',
        cleroWinner: true,
      },
      {
        name: 'Trip & vacation groups',
        clero: 'Auto-settles on trip end date',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'On-demand running group tabs',
        clero: '1-tap settle anytime',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Group balance simplification',
        clero: 'Included ($0 free)',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: '1-tap batch payment for all requests',
        clero: '1 bank transfer',
        competitor: 'Send 1-by-1',
        cleroWinner: true,
      },
      {
        name: 'Locked payment records',
        clero: 'Permanently locked',
        competitor: 'Not supported',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Receipts & Item Splitting',
    features: [
      {
        name: 'AI camera receipt scanner',
        clero: 'Auto-detects items & taxes',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Gmail & email receipt sync',
        clero: 'Auto-finds Uber & food delivery',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Smart card charge requests',
        clero: '1-tap from transactions',
        competitor: 'Manual typing only',
        cleroWinner: true,
      },
      {
        name: 'Friend web payment portal',
        clero: 'Pay on web without app',
        competitor: 'Requires registered bank',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Bank Settlement & Banking',
    features: [
      {
        name: 'Direct bank deposit',
        clero: 'Same-day to checking*',
        competitor: 'Direct bank transfer',
        cleroWinner: false,
      },
      {
        name: 'Transfer fees',
        clero: '$0 always',
        competitor: '$0 always',
        cleroWinner: false,
      },
      {
        name: 'Middleman wallet balance',
        clero: 'No wallet (direct to bank)',
        competitor: 'No wallet (direct to bank)',
        cleroWinner: false,
      },
      {
        name: 'Universal US bank support',
        clero: 'Thousands of US banks (Plaid)',
        competitor: 'Partner banks only',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Mobile Experience & Security',
    features: [
      {
        name: 'Dedicated mobile app experience',
        clero: 'Fast, dedicated app',
        competitor: 'Buried in bank apps',
        cleroWinner: true,
      },
      {
        name: 'Biometric security',
        clero: 'Face ID & Touch ID',
        competitor: 'Varies by bank app',
        cleroWinner: true,
      },
      {
        name: 'Multi-bank linking',
        clero: 'Link multiple accounts',
        competitor: 'Single primary bank',
        cleroWinner: true,
      },
    ],
  },
];

export const CompareZellePage: React.FC = () => {
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  return (
    <CompareShell competitorName="Zelle" competitorSlug="zelle">
      {/* HERO SECTION */}
      <section className="pt-6 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Clero vs Zelle
          </h1>
          <p className="mx-auto max-w-xl text-base sm:text-lg text-slate-600 font-medium">
            See how Clero compares to Zelle for everyday and group payments.
          </p>

          {/* HERO DOWNLOAD ACTION (FLIGHTY STYLE) */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />
          </div>
        </div>
      </section>

      {/* FULL COMPARISON MATRIX SECTION */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          {/* COMPREHENSIVE FLIGHTY-STYLE WRITE-UP */}
          <div className="rounded-3xl bg-white p-8 sm:p-12 mb-10 space-y-8">
            <div className="border-b border-slate-100 pb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                Direct bank speed combined with modern group intelligence
              </h2>
              <p className="mt-3 text-base text-slate-600 font-medium leading-relaxed">
                Zelle is great for simple one-on-one money transfers directly between bank accounts. But shared living, group trips, and restaurant bills involve multi-party calculations that Zelle was never designed to handle.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">1. Group tabs & balance netting</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Zelle only supports isolated 1-on-1 transfers. If five friends split an Airbnb and grocery runs, Zelle cannot track who paid or net the balances. Clero calculates the fewest payments needed so everyone settles in 1 single transfer.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">2. Free AI receipt scanning</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Zelle has no receipt tools. You have to open a calculator, figure out tax and tip percentages, and manually type dollar amounts. Clero photographs any bill, itemizes dishes automatically, and calculates proportional tax and tip to the exact cent.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">3. Universal bank support & web claiming</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Zelle requires supported partner banks and imposes arbitrary weekly limits. Clero links with thousands of US banks via Plaid and generates a web link so friends can pay securely from their browser without registration hurdles.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
              Feature breakdown
            </h2>
            <p className="mt-2 text-sm text-slate-600 font-medium">
              Side-by-side comparison between Clero and Zelle.
            </p>
          </div>

          <div className="overflow-hidden rounded-3xl bg-white">
            {/* TABLE TOP HEADER WITH APP LOGOS */}
            <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-100 items-center bg-slate-50/50">
              <div className="col-span-6 sm:col-span-5">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Features</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/clero_icon.png" alt="Clero" className="h-6 w-6 rounded-lg shrink-0 object-cover shadow-sm" />
                <span className="font-extrabold text-teal-950 text-xs sm:text-sm">Clero</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/logos/zelle.svg" alt="Zelle" className="h-6 w-6 rounded-lg shrink-0 shadow-sm" />
                <span className="font-bold text-slate-800 text-xs sm:text-sm">Zelle</span>
              </div>
            </div>

            {/* MATRIX CATEGORIES & CONCISE ROWS */}
            {ZELLE_MATRIX.map((section) => (
              <div key={section.category}>
                <div className="bg-slate-100/70 px-6 py-2.5">
                  <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800">
                    {section.category}
                  </h3>
                </div>

                <div className="divide-y divide-slate-100">
                  {section.features.map((row) => (
                    <div
                      key={row.name}
                      className="grid grid-cols-12 gap-3 px-6 py-3.5 items-center hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="col-span-6 sm:col-span-5">
                        <span className="text-xs sm:text-sm font-bold text-slate-900 block">{row.name}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 bg-teal-50/40 p-2 rounded-xl">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
                          <Check className="h-2.5 w-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs font-bold text-teal-950">{row.clero}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 p-2">
                        {row.cleroWinner ? (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <X className="h-2.5 w-2.5 stroke-[2.5]" />
                          </div>
                        ) : (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        )}
                        <span className="text-xs font-medium text-slate-600">{row.competitor}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* MID-PAGE CONVERSION CALLOUT (FLIGHTY STYLE) */}
          <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8 text-center space-y-3">
            <h3 className="text-lg sm:text-xl font-black text-slate-950">
              Ready for smarter group banking?
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
              Direct bank deposits, AI receipt scanning, and group balance netting with $0 fees.
            </p>
            <div className="flex justify-center pt-1">
              <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl rounded-3xl bg-white p-7 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-6 text-sm sm:text-base text-slate-700 font-medium divide-y divide-slate-100">
            <div className="pt-4 first:pt-0">
              <h3 className="text-base font-bold text-slate-950">How is Clero different from Zelle if both move money between banks?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Zelle only supports 1-on-1 transfers. It cannot track ongoing group tabs, itemize restaurant receipts, automate monthly roommate rent cycles, or simplify multiple group debts into a single payment.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Does Clero require my friends to use a specific partner bank?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Clero connects with thousands of US banks and credit unions through Plaid. Friends can also pay securely through a web link without checking bank network compatibility.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How does Clero handle roommate rent and recurring bills?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero lets you set up a Monthly group that tracks shared rent, utilities, and groceries throughout the month, automatically rolling over and settling on the 1st of every month.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Can I scan paper receipts in Zelle?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Zelle has no receipt scanning or itemization tools. Clero lets you take a photo of any receipt or sync Gmail to automatically itemize food and ride delivery orders.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Are there any fees to send or receive money in Clero?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Clero is 100% free with $0 transfer fees, $0 cashout fees, and $0 annual subscriptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER COMPARISONS QUICK JUMP */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <span className="text-xs font-extrabold tracking-widest uppercase text-slate-500">
            More comparisons
          </span>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/compare/splitwise')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Splitwise</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
            <button
              onClick={() => navigate('/compare/venmo')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Venmo</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
          </div>
        </div>
      </section>
    </CompareShell>
  );
};
export default CompareZellePage;
